/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */
import React, { useState } from 'react';
import { Button, Col, Collapse, FormGroup, Input, Row } from 'reactstrap';
import { useToggle } from '../../../hooks/useToggle';
import { RequestScope } from '../../../actions/api';
import classNames from 'classnames';
import { useSiteAlert } from '../../../hooks/useSiteAlert';
import { DataShareWithRequester } from '../../../types/dataShare';
import {
  canAccessData,
  convertScope,
  getDataScopeSurvey,
  getScopeAccess,
  getScopeOptions,
  getScopeViews,
} from '../../../utils/dataShare';
import { DataScopeGroups } from './DataScopeGroups';
import { DataShareCommonProps } from './PermissionsTemplate';
import { ButtonProps } from '@g17eco/molecules/toasts/CustomToast';
import { useRemoveDataShareMutation, useUndoRevokeDataShareMutation } from '../../../api/data-share';
import { generateErrorToast, generateToast } from '@g17eco/molecules/toasts';
import { useInitiativeRequestScopes } from '../hooks/useInitiativeRequestScopes';
import { PACK, SURVEY } from '@constants/terminology';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { DataScopeAccess, DataShareScopeView } from '../../../types/surveyDataScope';

export const TOOLTIP = {
  assuredData: (
    <span>
      Not available on <br /> your current plan
    </span>
  ),
  completedSurveys: (
    <span>
      If you use verification/verifier role
      <br />
      (Pro plan and above),
      <br />
      then only verified data is shared. <br />
      Non-verified data is not shared.
    </span>
  ),
  insights: (
    <span>
      Applies to: <br /> Overview page <br /> Environmental page <br /> Social page
      <br /> Governance page <br /> <br />
      Excludes:
      <br />
      Custom dashboards
    </span>
  ),
};

export enum DataShareTarget {
  CompletedSurveys = 'completed-surveys',
  OnlyAssuredData = 'only-assured-data',
}

interface LabelProps {
  children: JSX.Element | string;
}
export const Title = ({ children }: LabelProps) => (
  <label className='text-sm text-ThemeTextMedium text-uppercase lh-1'>{children}</label>
);

interface DataShareFormProps extends DataShareCommonProps {
  dataShare: DataShareWithRequester;
}

export const DataShareForm = ({ dataShare, metricGroups, actions, initiativeCombinedScope }: DataShareFormProps) => {
  const { addSiteError } = useSiteAlert();
  const dataScope = dataShare.dataScope;

  const accessData = canAccessData(dataScope);
  const shareAllPacks = getScopeAccess(dataScope) === DataScopeAccess.Full;
  const requestScopes = getScopeOptions(dataScope);
  const shareScopeViews = getScopeViews(dataScope);

  const [openSubControls, toggleSubControls, setSubControls] = useToggle(() => accessData);
  const [shareTarget, setShareTarget] = useState<DataShareTarget>(DataShareTarget.CompletedSurveys);

  const [removeDataShare, { isLoading: loadingRemove }] = useRemoveDataShareMutation();
  const [undoRevokeDataShare] = useUndoRevokeDataShareMutation();
  const initiativeRequestScopes = useInitiativeRequestScopes({ metricGroups, initiativeCombinedScope, requestScopes });

  const changeAccessData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    let dataScopeSurvey = getDataScopeSurvey(dataScope);
    if (checked) {
      dataScopeSurvey = {
        ...dataScopeSurvey,
        access: shareAllPacks ? DataScopeAccess.Full : DataScopeAccess.Partial,
      };
      if (!shareAllPacks) {
        dataScopeSurvey = {
          ...dataScopeSurvey,
          scope: convertScope(requestScopes),
        };
        setSubControls(true);
      }
    } else {
      dataScopeSurvey = {
        ...dataScopeSurvey,
        access: DataScopeAccess.None,
      };
    }

    await actions.update(dataShare, { survey: dataScopeSurvey });
  };

  const handleChangePacks = async (scopes: RequestScope[]) => {
    if (scopes.length === 0) {
      addSiteError(`You are unable to deselect all ${PACK.PLURAL}`);
      return;
    }

    const dataScopeSurvey = {
      ...getDataScopeSurvey(dataScope),
      scope: convertScope(scopes),
    };

    await actions.update(dataShare, { survey: dataScopeSurvey });
  };

  const handleChangeAllPacks = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    let dataScopeSurvey = {
      ...getDataScopeSurvey(dataScope),
      access: checked ? DataScopeAccess.Full : DataScopeAccess.Partial,
    };

    // if current share packs is empty then select all packs as default
    if (!checked && requestScopes.length === 0) {
      dataScopeSurvey = {
        ...dataScopeSurvey,
        scope: convertScope(initiativeRequestScopes),
      };
    }

    await actions.update(dataShare, { survey: dataScopeSurvey });
  };

  const handleShareScopeViews = async (scopeView: DataShareScopeView) => {
    let newScopeViews = [...shareScopeViews];
    const existedIndex = newScopeViews.findIndex((item) => item === scopeView);
    if (existedIndex < 0) {
      newScopeViews = [...newScopeViews, scopeView];
    } else {
      newScopeViews = newScopeViews.filter((item) => item !== scopeView);
    }
    const dataScopeSurvey = {
      ...getDataScopeSurvey(dataScope),
      views: newScopeViews,
    };

    await actions.update(dataShare, { survey: dataScopeSurvey });
  };

  const isDataShareEnabled = accessData || shareScopeViews.includes(DataShareScopeView.Messaging);

  const handleDelete = async () => {
    removeDataShare(dataShare._id)
      .unwrap()
      .then((deleted) =>
        generateToast({
          color: 'primary',
          icon: 'fa-regular fa-clock-rotate-left',
          buttonProps,
          title: 'Undo delete?',
          date: deleted.deletedDate ? new Date(deleted.deletedDate) : undefined,
        })
      )
      .catch((error) => {
        generateErrorToast(error);
      });
  };

  const handleUndo = async () => {
    undoRevokeDataShare(dataShare._id)
      .unwrap()
      .catch((error) => {
        generateErrorToast(error);
      });
  };

  const buttonProps: ButtonProps[] = [
    {
      text: 'Undo',
      handler: handleUndo,
      color: 'primary',
      outline: true,
      enforceRemove: true,
    },
  ];

  return (
    <div className='datashare-details'>
      <LoadingPlaceholder height={100} isLoading={loadingRemove}>
        <div className='d-flex justify-content-between'>
          <div className='d-flex flex-column'>
            <Title>Organisation</Title>
            <div className='mt-2 text-md text-ThemeTextDark'>{dataShare.requester.name}</div>
          </div>
          <div className='d-flex align-items-start gap-4 mt-4'>
            <FormGroup switch className='d-flex'>
              <Input
                id={`${dataShare._id}-can-access-data`}
                type='switch'
                className='mr-2'
                checked={accessData}
                onChange={changeAccessData}
              />
              <label htmlFor={`${dataShare._id}-can-access-data`} className='text-md'>
                Can access data
              </label>
            </FormGroup>
            <FormGroup switch className='d-flex'>
              <Input
                id={`${dataShare._id}-${DataShareScopeView.Messaging}`}
                type='switch'
                className='mr-2'
                onChange={() => handleShareScopeViews(DataShareScopeView.Messaging)}
                checked={shareScopeViews.includes(DataShareScopeView.Messaging)}
              />
              <label htmlFor={`${dataShare._id}-${DataShareScopeView.Messaging}`} className='d-flex flex-column'>
                <span className='text-md'>Can direct message</span>
                <span className='text-xs text-ThemeTextMedium'>(DMs will go to all Adminstrators)</span>
              </label>
            </FormGroup>
          </div>
        </div>

        {accessData ? (
          <div className='w-100'>
            <Collapse isOpen={openSubControls}>
              <div className='d-flex justify-content-center mt-3'>
                <div className='advanced-settings-divider' />
              </div>

              <Row className='pt-4'>
                <Col xs={12} md={5} className='d-flex flex-column text-ThemeTextMedium'>
                  <Title>Access to</Title>
                  <div className='d-flex align-items-center mt-2'>
                    <Input
                      id={`${dataShare._id}-${DataShareTarget.CompletedSurveys}`}
                      type='radio'
                      name={`${dataShare._id}-shareTarget`}
                      className='mr-2'
                      onChange={() => setShareTarget(DataShareTarget.CompletedSurveys)}
                      checked={shareTarget === DataShareTarget.CompletedSurveys}
                    />
                    <label htmlFor={`${dataShare._id}-${DataShareTarget.CompletedSurveys}`}>
                      Data from Completed {SURVEY.PLURAL}
                      <SimpleTooltip text={TOOLTIP.completedSurveys}>
                        <i className='fa-light fa-circle-info ml-1' />
                      </SimpleTooltip>
                    </label>
                  </div>
                  <div className='d-flex align-items-center mt-2'>
                    <SimpleTooltip text={TOOLTIP.assuredData}>
                      <Input
                        id={`${dataShare._id}-${DataShareTarget.OnlyAssuredData}`}
                        type='radio'
                        name={`${dataShare._id}-shareTarget`}
                        className='mr-2 background-ThemeBgDark'
                        onChange={() => setShareTarget(DataShareTarget.OnlyAssuredData)}
                        checked={shareTarget === DataShareTarget.OnlyAssuredData}
                        disabled
                      />
                    </SimpleTooltip>
                    <label
                      htmlFor={`${dataShare._id}-${DataShareTarget.OnlyAssuredData}`}
                      className={classNames({ 'text-ThemeTextLight': true })}
                    >
                      {`Only ‘Assured’ data from Completed ${SURVEY.PLURAL}`}
                    </label>
                  </div>
                </Col>

                <Col xs={12} md={4}>
                  <DataScopeGroups
                    htmlId={dataShare._id}
                    onChange={handleChangeAllPacks}
                    isFullAccess={shareAllPacks}
                    requestScopes={requestScopes}
                    handleChangePacks={handleChangePacks}
                    initiativeRequestScopes={initiativeRequestScopes}
                  />
                </Col>

                <Col xs={12} md={3} className='d-flex flex-column text-ThemeTextMedium'>
                  <Title>Access to pages</Title>
                  <FormGroup switch className='d-flex align-items-center mt-2'>
                    <Input
                      id={`${dataShare._id}-${DataShareScopeView.Survey}`}
                      type='switch'
                      className='mr-2'
                      onChange={() => handleShareScopeViews(DataShareScopeView.Survey)}
                      checked={shareScopeViews.includes(DataShareScopeView.Survey)}
                    />
                    <label htmlFor={`${dataShare._id}-${DataShareScopeView.Survey}`}>{SURVEY.CAPITALIZED_SINGULAR} overview</label>
                  </FormGroup>
                  <FormGroup switch className='d-flex align-items-center mt-2'>
                    <Input
                      id={`${dataShare._id}-${DataShareScopeView.Insights}`}
                      type='switch'
                      className='mr-2'
                      onChange={() => handleShareScopeViews(DataShareScopeView.Insights)}
                      checked={shareScopeViews.includes(DataShareScopeView.Insights)}
                    />
                    <label htmlFor={`${dataShare._id}-${DataShareScopeView.Insights}`}>
                      Insights
                      <SimpleTooltip text={TOOLTIP.insights}>
                        <i className='fa-light fa-circle-info ml-1' />
                      </SimpleTooltip>
                    </label>
                  </FormGroup>
                  <FormGroup switch className='d-flex align-items-center mt-2'>
                    <Input
                      id={`${dataShare._id}-${DataShareScopeView.Downloads}`}
                      type='switch'
                      className='mr-2'
                      onChange={() => handleShareScopeViews(DataShareScopeView.Downloads)}
                      checked={shareScopeViews.includes(DataShareScopeView.Downloads)}
                    />
                    <label htmlFor={`${dataShare._id}-${DataShareScopeView.Downloads}`}>Downloads</label>
                  </FormGroup>
                </Col>
              </Row>
            </Collapse>
          </div>
        ) : null}
        <div className='d-flex justify-content-between mt-4'>
          <SimpleTooltip
            text={
              isDataShareEnabled
                ? 'Please turn off data sharing access (all toggle buttons) before deleting the organisation'
                : undefined
            }
          >
            <Button
              outline={!isDataShareEnabled}
              disabled={isDataShareEnabled}
              color={'danger'}
              onClick={handleDelete}
              className='text-sm px-2'
            >
              <i className='fa-light fa-trash-can text-sm'></i>
            </Button>
          </SimpleTooltip>
          {accessData ? (
            <Button onClick={toggleSubControls} className='text-sm text-ThemeTextMedium px-2 border-0 bg-transparent'>
              Advanced settings{' '}
              <i className={`fa-light ${openSubControls ? 'fa-angle-up' : 'fa-angle-down'} text-sm ml-2`}></i>
            </Button>
          ) : null}
        </div>
      </LoadingPlaceholder>
    </div>
  );
};
